import { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';

/** Standard section header + status pill. */
export function SectionHeader({ title, description, actions }) {
  return (
    <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
      <div>
        <h1
          className="font-display text-3xl md:text-4xl text-brand-ink"
          data-testid="section-title"
        >
          {title}
        </h1>
        {description && (
          <p className="mt-1 text-sm text-brand-ink/70 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Inline save button + status. Wire via `useSaver()`. */
export function SaveBar({ saving, error, saved, onSave, dataTestId = 'save' }) {
  return (
    <div className="flex items-center gap-3">
      <Button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full disabled:opacity-70"
        data-testid={dataTestId}
      >
        {saving ? (
          <>Saving <Loader2 className="w-4 h-4 ml-2 animate-spin" /></>
        ) : (
          'Save changes'
        )}
      </Button>
      {saved && (
        <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
          <CheckCircle2 className="w-4 h-4" /> Saved
        </span>
      )}
      {error && (
        <span className="inline-flex items-center gap-1 text-sm text-red-700" role="alert">
          <AlertCircle className="w-4 h-4" /> {error}
        </span>
      )}
    </div>
  );
}

/** Compact hook to manage save state. */
export function useSaver() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!saved) return;
    const id = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(id);
  }, [saved]);

  const run = async (fn) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await fn();
      setSaved(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Save failed. Try again.';
      setError(typeof detail === 'string' ? detail : 'Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return { saving, saved, error, run };
}

/** Confirm-before-delete inline prompt. */
export function ConfirmDelete({ onConfirm, label = 'Delete' }) {
  const [ask, setAsk] = useState(false);
  if (!ask) {
    return (
      <button
        type="button"
        onClick={() => setAsk(true)}
        className="text-sm text-red-600 hover:underline"
      >
        {label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="text-brand-ink/80">Are you sure?</span>
      <button
        type="button"
        onClick={() => {
          setAsk(false);
          onConfirm();
        }}
        className="font-semibold text-red-600 hover:underline"
      >
        Yes, delete
      </button>
      <span className="text-brand-ink/40">|</span>
      <button
        type="button"
        onClick={() => setAsk(false)}
        className="text-brand-ink/70 hover:underline"
      >
        Cancel
      </button>
    </span>
  );
}

/** Basic labelled field wrapper. */
export function Field({ label, hint, children, cols = 1 }) {
  return (
    <label
      className={`block ${cols === 2 ? 'md:col-span-2' : ''}`}
    >
      <span className="text-sm font-medium text-brand-ink">{label}</span>
      {hint && <span className="ml-2 text-xs text-brand-ink/50">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/** Bare card wrapper. */
export function Card({ children, className = '' }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-brand-ink/10 p-5 md:p-6 ${className}`}
    >
      {children}
    </div>
  );
}
